/**
 * Real-time lighting analysis from webcam frames (canvas pixel sampling).
 */

const ANALYSIS_WIDTH = 160;
const ANALYSIS_HEIGHT = 120;
const MIN_FRAME_LUMINANCE = 52;
const MIN_FACE_LUMINANCE = 44;
const MAX_FRAME_LUMINANCE = 228;
const MAX_FACE_LUMINANCE = 242;
const OVEREXPOSED_LUMINANCE = 250;
const OVEREXPOSED_RATIO_THRESHOLD = 0.16;
const MIN_FACE_CONTRAST = 14;

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

const luminanceFromRgb = (r, g, b) => 0.299 * r + 0.587 * g + 0.114 * b;

const analyzeRegion = (data, width, height, region = null) => {
  let sum = 0;
  let count = 0;
  let overexposedCount = 0;
  let minL = 255;
  let maxL = 0;

  const xStart = region ? Math.floor(region.minX * width) : 0;
  const xEnd = region ? Math.ceil(region.maxX * width) : width;
  const yStart = region ? Math.floor(region.minY * height) : 0;
  const yEnd = region ? Math.ceil(region.maxY * height) : height;

  for (let y = yStart; y < yEnd; y += 1) {
    for (let x = xStart; x < xEnd; x += 1) {
      const idx = (y * width + x) * 4;
      const lum = luminanceFromRgb(data[idx], data[idx + 1], data[idx + 2]);
      sum += lum;
      count += 1;
      minL = Math.min(minL, lum);
      maxL = Math.max(maxL, lum);
      if (lum >= OVEREXPOSED_LUMINANCE) overexposedCount += 1;
    }
  }

  if (!count) {
    return {
      mean: 0,
      contrast: 0,
      overexposedRatio: 0,
    };
  }

  return {
    mean: sum / count,
    contrast: maxL - minL,
    overexposedRatio: overexposedCount / count,
  };
};

/**
 * @param {HTMLVideoElement} video
 * @param {HTMLCanvasElement} canvas
 * @param {{ minX: number, minY: number, maxX: number, maxY: number } | null} faceBounds normalized 0-1
 */
export const analyzeLightingFromVideo = (video, canvas, faceBounds = null) => {
  if (!video?.videoWidth || !video?.videoHeight || !canvas) {
    return {
      isValid: false,
      status: 'checking',
      guidance: 'Checking Lighting',
      frameMean: 0,
      faceMean: null,
      score: 0,
    };
  }

  const ctx = canvas.getContext('2d', { willReadFrequently: true });
  if (!ctx) {
    return {
      isValid: false,
      status: 'checking',
      guidance: 'Checking Lighting',
      frameMean: 0,
      faceMean: null,
      score: 0,
    };
  }

  canvas.width = ANALYSIS_WIDTH;
  canvas.height = ANALYSIS_HEIGHT;
  ctx.drawImage(video, 0, 0, ANALYSIS_WIDTH, ANALYSIS_HEIGHT);

  let imageData;
  try {
    imageData = ctx.getImageData(0, 0, ANALYSIS_WIDTH, ANALYSIS_HEIGHT);
  } catch {
    return {
      isValid: false,
      status: 'checking',
      guidance: 'Checking Lighting',
      frameMean: 0,
      faceMean: null,
      score: 0,
    };
  }

  const frameStats = analyzeRegion(imageData.data, ANALYSIS_WIDTH, ANALYSIS_HEIGHT);
  const normalizedBounds = faceBounds
    ? {
        minX: clamp(faceBounds.minX, 0, 1),
        minY: clamp(faceBounds.minY, 0, 1),
        maxX: clamp(faceBounds.maxX ?? faceBounds.minX + faceBounds.width, 0, 1),
        maxY: clamp(faceBounds.maxY ?? faceBounds.minY + faceBounds.height, 0, 1),
      }
    : null;

  const faceStats = normalizedBounds
    ? analyzeRegion(imageData.data, ANALYSIS_WIDTH, ANALYSIS_HEIGHT, normalizedBounds)
    : null;

  const frameMean = frameStats.mean;
  const faceMean = faceStats?.mean ?? null;
  const overexposedRatio = Math.max(
    frameStats.overexposedRatio,
    faceStats?.overexposedRatio ?? 0
  );

  if (
    frameMean >= MAX_FRAME_LUMINANCE ||
    (faceMean !== null && faceMean >= MAX_FACE_LUMINANCE) ||
    overexposedRatio >= OVEREXPOSED_RATIO_THRESHOLD
  ) {
    return {
      isValid: false,
      status: 'bright',
      guidance: 'Too Bright',
      frameMean,
      faceMean,
      score: clamp(1 - (frameMean - MAX_FRAME_LUMINANCE) / 40, 0, 0.4),
    };
  }

  if (frameMean < MIN_FRAME_LUMINANCE || (faceMean !== null && faceMean < MIN_FACE_LUMINANCE)) {
    return {
      isValid: false,
      status: 'low',
      guidance: 'Lighting Too Low',
      frameMean,
      faceMean,
      score: clamp(frameMean / MIN_FRAME_LUMINANCE, 0, 0.45),
    };
  }

  if (
    faceMean !== null &&
    faceStats &&
    faceStats.contrast < MIN_FACE_CONTRAST &&
    frameMean < MIN_FRAME_LUMINANCE + 18
  ) {
    return {
      isValid: false,
      status: 'low',
      guidance: 'Lighting Too Low',
      frameMean,
      faceMean,
      score: clamp(faceStats.contrast / MIN_FACE_CONTRAST, 0, 0.4),
    };
  }

  const frameScore = clamp((frameMean - MIN_FRAME_LUMINANCE) / (MAX_FRAME_LUMINANCE - MIN_FRAME_LUMINANCE), 0, 1);
  const faceScore =
    faceMean === null
      ? frameScore
      : clamp((faceMean - MIN_FACE_LUMINANCE) / (MAX_FACE_LUMINANCE - MIN_FACE_LUMINANCE), 0, 1);

  return {
    isValid: true,
    status: 'good',
    guidance: 'Lighting Good',
    frameMean,
    faceMean,
    score: clamp((frameScore * 0.4) + (faceScore * 0.6), 0, 1),
  };
};

export const mergeEnrollmentValidation = (faceValidation, lightingValidation) => {
  const lighting = lightingValidation || {
    isValid: false,
    status: 'checking',
    guidance: 'Checking Lighting',
  };

  if (!lighting.isValid) {
    return {
      ...faceValidation,
      isValid: false,
      guidance: lighting.guidance,
      lighting,
      faceReady: false,
    };
  }

  return {
    ...faceValidation,
    lighting,
    faceReady: Boolean(faceValidation?.isValid),
    isValid: Boolean(faceValidation?.isValid),
  };
};

export const getLightingStatusLabel = (lighting) => {
  if (!lighting) return 'Checking';
  if (lighting.status === 'good') return 'Good';
  if (lighting.status === 'low') return 'Too Low';
  if (lighting.status === 'bright') return 'Too Bright';
  return 'Checking';
};

export const getFaceStatusFromValidation = (validation) => {
  if (!validation) return { status: 'waiting', label: 'Waiting' };
  if (validation.faceCount === 0) return { status: 'none', label: 'Not Detected' };
  if (validation.faceCount > 1) return { status: 'multiple', label: 'Multiple Faces' };
  if (validation.isValid) return { status: 'ready', label: 'Ready' };
  return { status: 'adjusting', label: 'Adjusting' };
};
